import {
  CalendarEvents,
  Command,
  CustomEvent,
  fullDayMonthDateString,
  HolidayEvent,
  isShiftEvent,
  Shift,
  StorePreferences,
  Task,
  TimeOffEvent,
  toDateString,
  Uri,
  UserRole,
} from '@nextforce/api'
import { makeStyles, Text } from '@rneui/themed'
import { onlineManager } from '@tanstack/react-query'
import { t } from 'app/i18l'
import CalendarSyncModal from 'calendar_sync/calendar_sync_modal'
import { Image } from 'expo-image'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Dimensions, FlatList, Platform, View, ViewToken } from 'react-native'
import { Menu } from 'react-native-popup-menu'
import { MultipleRatingsScreen } from 'schedule/ratings/multiple_ratings/screen'
import { RatingScreen } from 'schedule/ratings/single_rating/screen'
import { WeeklySummaryMenu } from 'schedule/weekly_summary/weekly_summary_menu'
import { FloatingActionButton, Item } from 'shared/components/fab'
import { NoInternetText } from 'shared/components/no_internet_message'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { WEB_SCREEN_WIDTH } from 'shared/constants'
import {
  calculateShiftScrollOffset,
  ProcessedEvent,
  processEvents,
  SECTION_HEADER_HEIGHT,
} from '../../utils/schedule'
import CalendarModal from '../calendar'
import { EventView } from '../event'
import { isShiftInSummary } from '../event/shifts_group'
import { EmptyDate } from './empty_date'
import { EventSkeleton } from './skeleton'
import { MarkedDates, WeekView } from './week'

interface Props {
  events: CalendarEvents | undefined
  role: UserRole
  showMarkedDates: boolean
  hasNextWeek: boolean
  hasPreviousWeek: boolean
  fetchingNextWeek: boolean
  fetchingPreviousWeek: boolean
  currentDate: string | undefined
  storePreferences: StorePreferences
  onPressTimeOffSummary: (storeId: string) => void
  onHelperPressPendingTimeOff: (timeOffEvent: TimeOffEvent) => void
  onRequestTimeOff?: () => void
  onRequestNonAvailability?: () => void
  onCalendarDateSelected: (date: Date) => void
  onNextWeek: () => void
  onPreviousWeek: () => void
  onGotoShiftDetails: (shift: Shift) => void
  onGotoNextShifts: (date?: string | undefined) => void
  onAddShift?: (isAddAndAssign: boolean) => void
  onCloseRatingMenu: () => void
  onCommandButtonPress: (command: Command) => Promise<void>
  onAddCustomEvent?: () => void
  onEditCustomEvent: (customEvent: CustomEvent) => void
  onEditHolidayEvent: (holidayEvent: HolidayEvent) => void
  storeId?: string
  isMyShiftsSchedule?: boolean
  performOpenShiftAction: (command: Command) => Promise<Shift | undefined>
  isOpenShiftsSchedule?: boolean
  showSummaryTiles: boolean
  showNoShiftsCard: (openShiftsCount?: number | undefined) => boolean
  showWeeklySummary: boolean
  setShowWeeklySummary: React.Dispatch<React.SetStateAction<boolean>>
  showWeeklySummaryOption?: boolean
  showCrewRatingMenu?: boolean
  showStoreRatingMenu?: boolean
  showMultipleShiftsRatingMenu?: boolean
  uri?: Uri
  openShiftsScheduleEvents?: CalendarEvents
  showCustomEvents?: boolean
  shiftId?: string
  onAddTask?: () => void
  onPressTask: (task: Task) => void
}

interface DayViewData {
  date: string
  events: ProcessedEvent[]
}

const WEEKLY_SUMMARY_UPDATE_DAY = 2 // Tuesday

export default function ScheduleView({
  events: calendarEvents,
  role,
  showMarkedDates,
  hasNextWeek,
  hasPreviousWeek,
  fetchingNextWeek,
  fetchingPreviousWeek,
  currentDate,
  storePreferences,
  onPressTimeOffSummary,
  onHelperPressPendingTimeOff,
  onRequestTimeOff,
  onRequestNonAvailability,
  onCalendarDateSelected,
  onNextWeek,
  onPreviousWeek,
  onGotoShiftDetails,
  onGotoNextShifts,
  onAddShift,
  onCloseRatingMenu,
  onCommandButtonPress,
  onAddCustomEvent,
  onEditCustomEvent,
  onPressTask,
  onEditHolidayEvent,
  storeId,
  isMyShiftsSchedule,
  performOpenShiftAction,
  isOpenShiftsSchedule,
  showSummaryTiles,
  showNoShiftsCard,
  showWeeklySummary,
  setShowWeeklySummary,
  showWeeklySummaryOption,
  showCrewRatingMenu,
  showStoreRatingMenu,
  showMultipleShiftsRatingMenu,
  uri,
  openShiftsScheduleEvents,
  showCustomEvents,
  shiftId,
  onAddTask,
}: Props) {
  const styles = useStyles()
  const listRef = useRef<FlatList<ProcessedEvent>>(null)
  const weeklySummaryMenuRef = useRef<Menu>(null)
  const ratingMenuRef = useRef<Menu>(null)
  const multipleRatingMenuRef = useRef<Menu>(null)
  const hasScrolledToShift = useRef(false)
  const [expandedEvents, setExpandedEvents] = useState(
    () => new Map<string, boolean>()
  )
  const [calendarVisible, setCalendarVisible] = useState(false)
  const { width: SCREEN_WIDTH } = Dimensions.get('window')
  const width =
    Platform.OS !== 'web'
      ? SCREEN_WIDTH
      : WEB_SCREEN_WIDTH < SCREEN_WIDTH
      ? WEB_SCREEN_WIDTH
      : SCREEN_WIDTH
  const horizontalListRef = useRef<FlatList>(null)
  const eventHeightCacheRef = useRef(new Map<string, number>())
  const currentIndexRef = useRef(1) // Start in the middle (index 1)
  // Guards to ensure we process only one date change per user swipe
  const isUserMomentumRef = useRef(false)

  const viewabilityConfig = useMemo(
    () => ({
      itemVisiblePercentThreshold: 80,
    }),
    []
  )

  const eventsByDate: Map<string, ProcessedEvent[]> = useMemo(() => {
    if (!calendarEvents) {
      return new Map()
    }

    const processedEvents = processEvents(
      eventHeightCacheRef.current,
      calendarEvents,
      role
    )
    const byDate = new Map<string, ProcessedEvent[]>()

    processedEvents.forEach(event => {
      const dateEvents = byDate.get(event.dateString) || []
      dateEvents.push(event)
      byDate.set(event.dateString, dateEvents)
    })

    return byDate
  }, [calendarEvents, role])

  const generateDayViews = useCallback(
    (date: string, events: ProcessedEvent[] = []): DayViewData[] => {
      const currentMoment = moment(date)
      const currentDateString = toDateString(currentMoment)

      const prevMoment = moment(date).subtract(1, 'day')
      const nextMoment = moment(date).add(1, 'day')

      const prevDateString = toDateString(prevMoment)
      const nextDateString = toDateString(nextMoment)

      return [
        {
          date: prevDateString,
          events: [],
        },
        {
          date: currentDateString,
          events:
            events.length > 0
              ? events
              : eventsByDate.get(currentDateString) || [],
        },
        {
          date: nextDateString,
          events: [],
        },
      ]
    },
    [eventsByDate]
  )

  const [dayViews, setDayViews] = useState<DayViewData[]>(() => {
    const current = currentDate || toDateString(new Date())
    return generateDayViews(current)
  })

  const handleViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      if (viewableItems.length === 0) return

      const viewableItem = viewableItems[0]
      const viewableIndex = viewableItem.index

      if (viewableIndex !== null && viewableIndex !== currentIndexRef.current) {
        currentIndexRef.current = viewableIndex
      }
    },
    []
  )

  const handleShiftDetails = useCallback(
    (shift: Shift) => {
      onGotoShiftDetails(shift)
    },
    [onGotoShiftDetails]
  )

  const handleDateSelected = useCallback(
    (date: Date) => {
      onCalendarDateSelected?.(date)
    },
    [onCalendarDateSelected]
  )

  const handleMomentumEnd = useCallback(() => {
    // Only handle momentum end if it was triggered by a user gesture
    if (!isUserMomentumRef.current) {
      return
    }

    // Consume the gesture token so subsequent end events are ignored
    isUserMomentumRef.current = false

    // Get the current visible item's date
    const index = currentIndexRef.current
    const visibleDate = dayViews[index]?.date

    if (visibleDate) {
      handleDateSelected(moment(visibleDate).toDate())
    }
  }, [dayViews, handleDateSelected])

  const weekStartDate = useMemo(() => {
    if (!calendarEvents) return toDateString(new Date())
    return Object.keys(calendarEvents).sort()[0]
  }, [calendarEvents])

  const handleEventExpand = useCallback((eventId: string) => {
    setExpandedEvents(prev => {
      const next = new Map(prev)
      next.set(eventId, !prev.get(eventId))
      return next
    })
  }, [])

  const currentDateEvents = useMemo(() => {
    const dateString = toDateString(currentDate)
    return eventsByDate.get(dateString) || []
  }, [eventsByDate, currentDate])

  const markedDates: MarkedDates = useMemo(() => {
    if (!calendarEvents || !showMarkedDates) return []
    return Object.entries(calendarEvents).map(([date, events]) => ({
      date,
      dots: [
        events.some(event => event.busy)
          ? { color: styles.dot.color }
          : { color: styles.invisibleDot.color },
      ],
    }))
  }, [
    calendarEvents,
    showMarkedDates,
    styles.dot.color,
    styles.invisibleDot.color,
  ])

  const weeklySummaryDate = useMemo(() => {
    if (!weekStartDate) return undefined
    return moment(weekStartDate).add(WEEKLY_SUMMARY_UPDATE_DAY, 'days').toDate()
  }, [weekStartDate])

  // Handle weekly summary state changes
  useEffect(() => {
    if (showWeeklySummary && weeklySummaryDate) {
      const newDate = weeklySummaryDate
      onCalendarDateSelected?.(newDate)

      // Then open the menu after a short delay to ensure date change is processed
      const timer = setTimeout(async () => {
        if (
          weeklySummaryMenuRef.current &&
          !weeklySummaryMenuRef.current.isOpen()
        ) {
          try {
            await weeklySummaryMenuRef.current.open()
          } catch (e) {
            console.error('Failed to open weekly summary menu:', e)
          }
        }
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [showWeeklySummary, weeklySummaryDate, onCalendarDateSelected])

  // Reset scroll state when shiftId changes
  useEffect(() => {
    if (shiftId) {
      hasScrolledToShift.current = false
    }
  }, [shiftId])

  const scrollToShift = useCallback(
    (shift: Shift) => {
      const offset = calculateShiftScrollOffset(
        shift,
        eventsByDate.get(toDateString(currentDate)),
        expandedEvents,
        showSummaryTiles,
        role
      )

      if (offset >= 0) {
        listRef.current?.scrollToOffset({ offset, animated: true })
        hasScrolledToShift.current = true
      }
    },
    [currentDate, eventsByDate, expandedEvents, role, showSummaryTiles]
  )

  useEffect(() => {
    if (
      shiftId &&
      currentDateEvents.length > 0 &&
      !hasScrolledToShift.current
    ) {
      const eventWithShift = currentDateEvents.find(
        event =>
          isShiftEvent(event) &&
          event.shifts.some(shift => shift.id === shiftId)
      )

      const shift = isShiftEvent(eventWithShift)
        ? eventWithShift.shifts.find(s => s.id === shiftId)
        : undefined

      if (eventWithShift && shift) {
        if (isShiftInSummary(shift)) {
          if (!expandedEvents.get(eventWithShift.id)) {
            setExpandedEvents(prev => {
              const next = new Map(prev)
              next.set(eventWithShift.id, true)
              return next
            })
          }
          // For shifts in summary, wait longer to ensure expansion animation completes
          setTimeout(() => {
            scrollToShift(shift)
          }, 300) // Wait for expansion animation
        } else {
          // For regular shifts, just wait for next frame
          requestAnimationFrame(() => {
            scrollToShift(shift)
          })
        }
      }
    }
  }, [currentDateEvents, expandedEvents, scrollToShift, shiftId])

  const shouldShowWeeklySummary = useCallback(
    (date: string) => {
      if (!showWeeklySummaryOption || !weekStartDate || !weeklySummaryDate)
        return false
      const currentDateMoment = moment(date)
      return currentDateMoment.isSame(weeklySummaryDate, 'day')
    },
    [showWeeklySummaryOption, weekStartDate, weeklySummaryDate]
  )

  useEffect(() => {
    if (currentDate) {
      // Generate new day views with the current date in the middle
      const newViews = generateDayViews(currentDate, currentDateEvents)
      setDayViews(newViews)

      // Scroll to the middle position
      horizontalListRef.current?.scrollToIndex({ index: 1, animated: false })
      // Reset tracked index to the middle after re-centering
      currentIndexRef.current = 1
    }
  }, [currentDate, currentDateEvents, generateDayViews])

  const renderItem = useCallback(
    ({ item }: { item: ProcessedEvent }) => (
      <EventView
        key={item.key}
        event={item}
        role={role}
        expanded={expandedEvents.get(item.id) ?? false}
        onShiftDetails={handleShiftDetails}
        onEventExpand={handleEventExpand}
        performOpenShiftAction={performOpenShiftAction}
        onPressTimeOffSummary={onPressTimeOffSummary}
        onHelperPressPendingTimeOff={onHelperPressPendingTimeOff}
        onCommandButtonPress={onCommandButtonPress}
        onEditCustomEvent={onEditCustomEvent}
        onEditHolidayEvent={onEditHolidayEvent}
        showSummaryTiles={showSummaryTiles}
        onPressTask={onPressTask}
      />
    ),
    [
      role,
      expandedEvents,
      handleShiftDetails,
      handleEventExpand,
      performOpenShiftAction,
      onPressTimeOffSummary,
      onHelperPressPendingTimeOff,
      onCommandButtonPress,
      onEditCustomEvent,
      onEditHolidayEvent,
      showSummaryTiles,
      onPressTask,
    ]
  )

  const onPressGoToNextShifts = useCallback(
    () => onGotoNextShifts(currentDate),
    [currentDate, onGotoNextShifts]
  )

  const floatingActionButtonItems = useMemo(() => {
    const items: Item[] = []
    if (storePreferences.allowNewOpenShift) {
      items.push({
        name: 'addShift',
        title: t('addShift'),
        icon: 'plus',
      })
    }
    if (storePreferences.allowAddAndAssign) {
      items.push({
        name: 'addAndAssignShift',
        title: t('addAndAssign'),
        source: require('assets/routes/group/Group_3.png'),
      })
    }
    if (onRequestTimeOff) {
      items.push({
        name: 'requestTimeOff',
        title: t('requestTimeOff'),
        icon: 'calendar-times',
      })
    }
    if (onRequestNonAvailability) {
      items.push({
        name: 'requestNonAvailability',
        title: t('requestNonAvailability'),
        source: require('assets/routes/schedule/white-check.png'),
      })
    }
    if (showCustomEvents) {
      items.push({
        name: 'addCustomEvents',
        title: 'Add Event',
        icon: 'calendar-plus',
      })
    }
    if (storePreferences.enableCustomTasks) {
      items.push({
        name: 'addTask',
        title: t('addTask'),
        icon: 'tasks',
        type: 'font-awesome-5',
      })
    }
    return items
  }, [
    onRequestNonAvailability,
    onRequestTimeOff,
    showCustomEvents,
    storePreferences.allowAddAndAssign,
    storePreferences.allowNewOpenShift,
    storePreferences.enableCustomTasks,
  ])

  const onFloatingActionButtonItemPress = (name: string) => {
    switch (name) {
      case 'addShift':
        if (!onAddShift) {
          return
        }
        return onAddShift(false)
      case 'addAndAssignShift':
        if (!onAddShift) {
          return
        }
        return onAddShift(true)
      case 'requestTimeOff':
        if (!onRequestTimeOff) {
          return
        }
        return onRequestTimeOff()
      case 'requestNonAvailability':
        if (!onRequestNonAvailability) {
          return
        }
        return onRequestNonAvailability()
      case 'addCustomEvents':
        if (!onAddCustomEvent) {
          return
        }
        return onAddCustomEvent()
      case 'addTask':
        if (!onAddTask) {
          return
        }
        return onAddTask()
    }
  }

  const renderDayView = useCallback(
    ({ item }: { item: DayViewData }) => {
      const isCurrentDay = item.date === currentDate
      return (
        <View style={{ width }}>
          {shouldShowWeeklySummary(item.date) && (
            <WeeklySummaryMenu
              role={role}
              storeId={storeId}
              isHelperWeeklySummary={isMyShiftsSchedule}
              setShowWeeklySummary={setShowWeeklySummary}
              menuRef={weeklySummaryMenuRef}
            />
          )}
          <View style={styles.dateHeaderContainer}>
            <Text style={styles.dateHeader}>
              {fullDayMonthDateString(item.date)}
            </Text>
          </View>
          {isCurrentDay ? (
            item.events.length > 0 ? (
              <FlatList
                ref={listRef}
                data={item.events}
                renderItem={renderItem}
                keyExtractor={event => event.key}
                contentContainerStyle={styles.listContentContainer}
              />
            ) : (
              <EmptyDate
                onGotoNextShifts={onPressGoToNextShifts}
                hasNonShiftEvents={false}
                showNoShiftsCard={showNoShiftsCard(
                  openShiftsScheduleEvents?.[toDateString(currentDate)]?.length
                )}
              />
            )
          ) : (
            <EventSkeleton count={10} />
          )}
        </View>
      )
    },
    [
      currentDate,
      width,
      shouldShowWeeklySummary,
      role,
      storeId,
      isMyShiftsSchedule,
      setShowWeeklySummary,
      styles.dateHeaderContainer,
      styles.dateHeader,
      styles.listContentContainer,
      renderItem,
      onPressGoToNextShifts,
      showNoShiftsCard,
      openShiftsScheduleEvents,
    ]
  )

  const getItemLayout = useCallback(
    (_: any, index: number) => ({
      length: width,
      offset: width * index,
      index,
    }),
    [width]
  )

  return (
    <View style={styles.container}>
      <CalendarModal
        calendarVisible={calendarVisible}
        setCalendarVisible={setCalendarVisible}
        onCalendarDateSelected={handleDateSelected}
        markedDates={markedDates}
        startDate={weekStartDate}
      />
      <View style={styles.weekViewContainer}>
        <View style={styles.weekView}>
          <WeekView
            markedDates={markedDates}
            startDate={weekStartDate}
            hasNextWeek={hasNextWeek}
            hasPreviousWeek={hasPreviousWeek}
            fetchingNextWeek={fetchingNextWeek}
            fetchingPreviousWeek={fetchingPreviousWeek}
            onDateSelected={onCalendarDateSelected}
            onPreviousWeek={onPreviousWeek}
            onNextWeek={onNextWeek}
            highlightDate={currentDate}
          />
        </View>

        {!isOpenShiftsSchedule && (
          <PressableOpacity
            style={styles.calendarIconContainer}
            onPress={() => setCalendarVisible(true)}
          >
            <Image
              source={require('assets/routes/schedule/CalendarModal.png')}
              style={styles.calendarIcon}
              contentFit="contain"
            />
          </PressableOpacity>
        )}
      </View>
      <View style={styles.listContainer}>
        <FlatList
          ref={horizontalListRef}
          horizontal
          pagingEnabled={Platform.OS !== 'web'}
          data={dayViews}
          renderItem={renderDayView}
          keyExtractor={item => item.date}
          showsHorizontalScrollIndicator={false}
          initialScrollIndex={1}
          getItemLayout={getItemLayout}
          onMomentumScrollBegin={() => {
            // Mark that a user gesture started; allow a single processing on end
            isUserMomentumRef.current = true
          }}
          onMomentumScrollEnd={handleMomentumEnd}
          onViewableItemsChanged={handleViewableItemsChanged}
          viewabilityConfig={viewabilityConfig}
          removeClippedSubviews={true}
          scrollEnabled={Platform.OS !== 'web'}
        />
      </View>

      {isMyShiftsSchedule && <CalendarSyncModal />}

      {onlineManager.isOnline() && floatingActionButtonItems.length > 0 && (
        <FloatingActionButton
          key={storeId}
          items={floatingActionButtonItems}
          onPressItem={onFloatingActionButtonItemPress}
        />
      )}

      {uri && (
        <RatingScreen
          menuRef={ratingMenuRef}
          onCloseRatingMenu={onCloseRatingMenu}
          rateCrew={showCrewRatingMenu}
          rateStore={showStoreRatingMenu}
          uri={uri}
        />
      )}

      {showMultipleShiftsRatingMenu && (
        <MultipleRatingsScreen
          menuRef={multipleRatingMenuRef}
          onCloseRatingMenu={onCloseRatingMenu}
        />
      )}

      {!onlineManager.isOnline() && (
        <NoInternetText message={t('noInternetScheduleScreenMessage')} />
      )}
    </View>
  )
}

const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors?.grey6,
  },
  weekViewContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  weekView: {
    flex: 1,
  },
  listContainer: {
    flex: 1,
  },
  listContentContainer: { flexGrow: 1, paddingBottom: 100 },
  calendarIconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  calendarIcon: {
    tintColor: theme.colors?.grey2,
    width: 25,
    height: 25,
  },
  dot: {
    color: theme.colors?.grey3,
  },
  invisibleDot: {
    color: theme.mode === 'dark' ? theme.colors?.grey5 : theme.colors?.grey6,
  },
  dateHeader: {
    color: theme.colors?.white,
    paddingHorizontal: 16,
    fontSize: 14,
  },
  dateHeaderContainer: {
    height: SECTION_HEADER_HEIGHT,
    justifyContent: 'center',
    backgroundColor: theme.colors?.grey2,
  },
}))
